import React, { useState, useRef, useEffect, useMemo, memo } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ImageViewerModal from '../../zenScreen/components/ImageViewerModal';
import { InlineImageText, FeedbackButtons, markdownComponents, GEN_TOOLS, isHiddenTool, getToolDisplayName, extractTextContent } from './chatMessageParts';
import { AssetApprovalCard, SystemMessageCard, GenerationStatusCard, CopyPackageCard } from './smartMessages';

function ChatMessage({
  message,
  completedImages = {},
  userImageUrls,
  isStreaming = false,
  devMode = false,
  onUseAsInput,
  onImageAction,
  onQuickReply,
  brandId,
  isLastMessage = false,
  openAssetPicker,
}) {
  const isUser = message.role === 'user';
  const parts = useMemo(() => message.parts || [], [message.parts]);

  const textParts = parts.filter(p => p.type === 'text');
  const reasoningParts = parts.filter(p => p.type === 'reasoning');
  const allToolParts = parts.filter(p => p.type?.startsWith('tool-') && p.toolCallId);
  // Dedup by toolCallId — keep output-available over input-available for the same call
  const seenCallIds = new Map();
  for (const p of allToolParts) {
    const existing = seenCallIds.get(p.toolCallId);
    if (!existing || p.state === 'output-available') seenCallIds.set(p.toolCallId, p);
  }
  // Filter out noisy/internal tools (shown in dev mode)
  const toolParts = [...seenCallIds.values()].filter(p => {
    const name = p.type?.startsWith('tool-') ? p.type.substring(5) : (p.type || '');
    return devMode || !isHiddenTool(name);
  });

  // Collect all generation-started events from the stream (multi-format emits one per job)
  const generationStartedParts = parts.filter(p => p.type === 'data-generation-started');
  const stepMetricsParts = parts.filter(p => p.type === 'data-step-metrics');
  const userQuestionsParts = parts.filter(p => p.type === 'data-user-questions');
  const assetVerifyParts = parts.filter(p => p.type === 'data-asset-verify');

  // The data-generation-started fallback jobId — same fallback ToolCallCard uses at render time.
  // ToolCallCard falls back to this when a gen tool's own output has no image_job_id
  // (e.g. agent-runnerAgent returns {text:"..."} with no image_job_id).
  // The dedup must use the same fallback so agent-runnerAgent deduplicates against run-model.
  const generationStartedJobId =
    generationStartedParts[0]?.data?.image_job_id ||
    generationStartedParts[0]?.data?.workflow_job_id;

  // True when at least one generation tool exists in toolParts (pending or complete)
  const hasGenTool = toolParts.some(p => {
    const name = p.type?.startsWith('tool-') ? p.type.substring(5) : (p.type || '');
    return GEN_TOOLS.has(name);
  });

  // Job IDs already covered by a visible tool card (so we don't double-render)
  const coveredJobIds = new Set(
    toolParts.flatMap(p => {
      const out = p.output || {};
      return [
        out.image_job_id, out.workflow_job_id,
        out.data?.image_job_id, out.data?.workflow_job_id,
        ...(out.image_job_ids || []),
        ...(out.workflow_job_ids || []),
      ].filter(Boolean);
    })
  );
  // If any gen tool exists, treat ALL data-generation-started jobIds as covered.
  // This prevents ExtraGenerationCard from duplicating batch variations.
  if (hasGenTool) {
    for (const gp of generationStartedParts) {
      const jid = gp.data?.image_job_id || gp.data?.workflow_job_id;
      if (jid) coveredJobIds.add(jid);
    }
  }

  // Deduplicate generation tool cards by job ID.
  // Uses the same data-generation-started fallback as ToolCallCard so that
  // agent-runnerAgent (output={text:"..."}, no image_job_id) deduplicates correctly
  // against run-model (output has image_job_id). Without this, both resolve to the
  // same visual jobId at render time but the dedup misses agent-runnerAgent.
  const seenGenerationJobIds = new Set();
  const deduplicatedToolParts = toolParts.filter(p => {
    const out = p.output || {};
    const jid = out.image_job_id || out.workflow_job_id
      || out.data?.image_job_id || out.data?.workflow_job_id
      || out.image_job_ids?.[0] || out.workflow_job_ids?.[0]
      || generationStartedJobId;
    const name = p.type?.startsWith('tool-') ? p.type.substring(5) : (p.type || '');
    if (GEN_TOOLS.has(name) && jid) {
      if (seenGenerationJobIds.has(jid)) return false;
      seenGenerationJobIds.add(jid);
    }
    return true;
  });

  // Extra job IDs from data-generation-started events not already represented by a tool card.
  // Only used for multi-format jobs where individual output job IDs aren't in any tool card.
  const extraJobIds = generationStartedParts
    .map(p => ({ jobId: p.data?.image_job_id || p.data?.workflow_job_id, jobType: p.data?.job_type }))
    .filter(({ jobId }) => jobId && !coveredJobIds.has(jobId))
    .filter(({ jobId }, idx, arr) => arr.findIndex(x => x.jobId === jobId) === idx);

  const rawTextContent = textParts.length > 0
    ? textParts.map(p => p.text).join('')
    : extractTextContent(message.content);

  // ─── Build ordered segments for interleaved text/tool rendering ────────────
  // Groups consecutive text parts into one segment, tool parts become individual segments.
  // This preserves the original order: text → tool → text renders as 3 separate blocks.
  const orderedSegments = useMemo(() => {
    if (isUser) return []; // user messages don't need interleaving
    const segments = [];
    let pendingText = '';
    const dedupedCallIds = new Set(deduplicatedToolParts.map(p => p.toolCallId));

    for (const part of parts) {
      if (part.type === 'text') {
        pendingText += part.text || '';
      } else if (part.type === 'reasoning' || part.type?.startsWith('data-')) {
        // Skip — these are rendered in their own dedicated sections
        continue;
      } else if (part.type?.startsWith('tool-') && part.toolCallId) {
        // Only include if it's in our deduped+filtered list
        if (dedupedCallIds.has(part.toolCallId)) {
          const dedupedPart = deduplicatedToolParts.find(p => p.toolCallId === part.toolCallId);
          if (dedupedPart) {
            // Flush pending text before the tool
            if (pendingText.trim()) {
              segments.push({ type: 'text', content: pendingText });
              pendingText = '';
            }
            segments.push({ type: 'tool', part: dedupedPart });
            dedupedCallIds.delete(part.toolCallId); // don't re-add on duplicate
          }
        }
      }
    }
    // Flush remaining text
    if (pendingText.trim()) {
      segments.push({ type: 'text', content: pendingText });
    }
    return segments;
  }, [parts, deduplicatedToolParts, isUser]);

  // ─── <think> block parsing ─────────────────────────────────────────────────
  const thinkMatch = rawTextContent.match(/^<think>([\s\S]*?)<\/think>\s*/i);
  const isThinkingStreaming = !thinkMatch && rawTextContent.trimStart().startsWith('<think>');
  const thinkContent = thinkMatch
    ? thinkMatch[1].trim()
    : (isThinkingStreaming ? rawTextContent.replace(/^<think>/i, '').trim() : null);
  const textContent = thinkMatch
    ? rawTextContent.slice(thinkMatch[0].length)
    : (isThinkingStreaming ? '' : rawTextContent);

  // ─── AI SDK reasoning parts (native Gemini thinking) ─────────────────────
  const reasoningText = reasoningParts.map(p => p.reasoning || p.text || '').join('');
  const hasReasoning = reasoningText.length > 0;
  const isReasoningStreaming = hasReasoning && !textContent && !thinkContent;

  const thinkStartRef = useRef(null);
  const [thinkDurationSec, setThinkDurationSec] = useState(null);
  const [thinkExpanded, setThinkExpanded] = useState(false);
  const [reasoningExpanded, setReasoningExpanded] = useState(false);
  const reasoningStartRef = useRef(null);
  const [reasoningDurationSec, setReasoningDurationSec] = useState(null);

  useEffect(() => {
    if (isThinkingStreaming && !thinkStartRef.current) {
      thinkStartRef.current = Date.now();
    }
    if (thinkMatch && thinkStartRef.current && thinkDurationSec === null) {
      const ms = Date.now() - thinkStartRef.current;
      setThinkDurationSec(Math.max(1, Math.round(ms / 1000)));
      setThinkExpanded(false);
    }
  }, [rawTextContent]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isReasoningStreaming && !reasoningStartRef.current) {
      reasoningStartRef.current = Date.now();
    }
    if (hasReasoning && !isReasoningStreaming && reasoningStartRef.current && reasoningDurationSec === null) {
      const ms = Date.now() - reasoningStartRef.current;
      setReasoningDurationSec(Math.max(1, Math.round(ms / 1000)));
      setReasoningExpanded(false);
    }
  }, [reasoningText, isReasoningStreaming]); // eslint-disable-line react-hooks/exhaustive-deps

  const userImages = (message.imageUrls?.length > 0 ? message.imageUrls : userImageUrls || [])
    .map((item) => ({ url: typeof item === 'string' ? item : item.url }))
    .filter((a) => a.url);

  return (
    <div style={{
      display: 'flex',
      justifyContent: isUser ? 'flex-end' : 'flex-start',
      alignItems: 'flex-end',
      gap: 8,
      marginBottom: 16,
      maxWidth: '100%',
    }}>
      {/* Assistant avatar */}
      {!isUser && (
        <div style={{
          width: 30, height: 30, borderRadius: 10, flexShrink: 0,
          background: 'linear-gradient(135deg, #FF7E67, #FF4D8D)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 2px 12px rgba(255,126,103,0.3)',
          marginBottom: 2,
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        </div>
      )}

      <div style={{ maxWidth: isUser ? 'min(68%, 520px)' : 'min(82%, 720px)', minWidth: 0 }}>
        {/* User attached images */}
        {userImages.length > 0 && (
          <div style={{
            display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: devMode ? 4 : 6,
            justifyContent: isUser ? 'flex-end' : 'flex-start',
          }}>
            {userImages.map((a, i) => (
              <div key={i} style={{ position: 'relative', flexShrink: 0 }}>
                <img src={a.url} alt=""
                  style={{
                    maxHeight: 130, width: 'auto', maxWidth: 260, borderRadius: 12,
                    border: devMode ? '1px solid rgba(251,191,36,0.4)' : '1px solid rgba(255,255,255,0.1)',
                    objectFit: 'contain', display: 'block',
                  }}
                />
                {devMode && (
                  <span style={{
                    position: 'absolute', top: 5, left: 5,
                    background: 'rgba(251,191,36,0.85)', color: '#000',
                    fontSize: 8, fontWeight: 800, letterSpacing: '0.06em',
                    padding: '1px 5px', borderRadius: 4,
                  }}>VISION</span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Dev mode: image metadata panel */}
        {devMode && isUser && userImages.length > 0 && (
          <div style={{
            marginBottom: 6, padding: '6px 10px', borderRadius: 8,
            background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.2)',
            fontSize: 10, color: '#fbbf24', fontFamily: 'monospace',
            textAlign: 'left', maxWidth: '100%', wordBreak: 'break-all',
          }}>
            <div style={{ fontWeight: 700, marginBottom: 4, letterSpacing: '0.05em' }}>
              📎 {userImages.length} image{userImages.length > 1 ? 's' : ''} attached
            </div>
            {userImages.map((a, i) => (
              <div key={i} style={{ marginBottom: i < userImages.length - 1 ? 4 : 0 }}>
                <span style={{ opacity: 0.55 }}>#{i + 1} </span>
                <span style={{ color: '#86efac', fontWeight: 600 }}>✓ sent as VISION</span>
                <span style={{ opacity: 0.55 }}> + URL annotation</span>
                <div style={{ opacity: 0.7, marginTop: 1 }}>{a.url}</div>
              </div>
            ))}
          </div>
        )}

        {/* Native reasoning block — Gemini thinking via AI SDK reasoning parts */}
        {hasReasoning && (
          <div style={{ marginBottom: textContent ? 8 : 4 }}>
            <button
              onClick={() => setReasoningExpanded(!reasoningExpanded)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '4px 0', marginBottom: (reasoningExpanded || isReasoningStreaming) ? 6 : 0,
              }}
            >
              {isReasoningStreaming ? (
                <>
                  <span style={{
                    width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                    background: 'linear-gradient(135deg, #FF7E67, #FF4D8D)',
                    display: 'inline-block', animation: 'pulse 1s ease-in-out infinite',
                  }} />
                  <span style={{ fontSize: 12, color: 'rgba(255,126,103,0.75)', fontStyle: 'italic' }}>Thinking…</span>
                </>
              ) : (
                <>
                  <span style={{
                    fontSize: 10, color: 'rgba(255,255,255,0.25)',
                    transform: reasoningExpanded ? 'rotate(90deg)' : 'none',
                    transition: 'transform 150ms ease', display: 'inline-block',
                  }}>▶</span>
                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', userSelect: 'none' }}>
                    Thought for {reasoningDurationSec !== null ? `${reasoningDurationSec}s` : '…'}
                  </span>
                </>
              )}
            </button>
            {(reasoningExpanded || isReasoningStreaming) && (
              <div style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: 10, padding: '10px 14px',
                fontSize: 12, lineHeight: 1.65,
                color: 'rgba(255,255,255,0.4)',
                fontStyle: 'italic',
                maxHeight: 280, overflowY: 'auto',
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              }}>
                {reasoningText || <span style={{ opacity: 0.4 }}>…</span>}
                {isReasoningStreaming && (
                  <span style={{ display: 'inline-block', width: 6, height: 12, background: 'rgba(255,126,103,0.5)', marginLeft: 2, animation: 'pulse 0.8s ease-in-out infinite', verticalAlign: 'text-bottom' }} />
                )}
              </div>
            )}
          </div>
        )}

        {/* Thinking block — Claude-style collapsible (<think> tags in text) */}
        {thinkContent !== null && (
          <div style={{ marginBottom: textContent ? 8 : 4 }}>
            <button
              onClick={() => setThinkExpanded(!thinkExpanded)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '4px 0', marginBottom: (thinkExpanded || isThinkingStreaming) ? 6 : 0,
              }}
            >
              {isThinkingStreaming ? (
                <>
                  <span style={{
                    width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                    background: 'linear-gradient(135deg, #FF7E67, #FF4D8D)',
                    display: 'inline-block', animation: 'pulse 1s ease-in-out infinite',
                  }} />
                  <span style={{ fontSize: 12, color: 'rgba(255,126,103,0.75)', fontStyle: 'italic' }}>Thinking…</span>
                </>
              ) : (
                <>
                  <span style={{
                    fontSize: 10, color: 'rgba(255,255,255,0.25)',
                    transform: thinkExpanded ? 'rotate(90deg)' : 'none',
                    transition: 'transform 150ms ease', display: 'inline-block',
                  }}>▶</span>
                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', userSelect: 'none' }}>
                    Thought for {thinkDurationSec !== null ? `${thinkDurationSec}s` : '…'}
                  </span>
                </>
              )}
            </button>
            {(thinkExpanded || isThinkingStreaming) && (
              <div style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: 10, padding: '10px 14px',
                fontSize: 12, lineHeight: 1.65,
                color: 'rgba(255,255,255,0.4)',
                fontStyle: 'italic',
                maxHeight: 280, overflowY: 'auto',
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              }}>
                {thinkContent || <span style={{ opacity: 0.4 }}>…</span>}
                {isThinkingStreaming && (
                  <span style={{ display: 'inline-block', width: 6, height: 12, background: 'rgba(255,126,103,0.5)', marginLeft: 2, animation: 'pulse 0.8s ease-in-out infinite', verticalAlign: 'text-bottom' }} />
                )}
              </div>
            )}
          </div>
        )}

        {/* Interleaved text + tool rendering (preserves order) */}
        {isUser ? (
          // Smart user message detection
          textContent?.startsWith('Approved assets for image generation') ? (
            <AssetApprovalCard text={textContent} />
          ) : textContent?.match(/^\[?(URO_HIDDEN|System:|\[auto-critique)/i) ? (
            <SystemMessageCard text={textContent} devMode={devMode} />
          ) : textContent ? (
            <div style={{
              background: 'rgba(255,126,103,0.12)',
              border: '1px solid rgba(255,126,103,0.25)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 2px 16px rgba(0,0,0,0.15)',
              color: '#FFFFFF',
              borderRadius: '18px 18px 4px 18px',
              padding: '10px 15px',
              fontSize: 14, lineHeight: 1.65, wordBreak: 'break-word',
            }}>
              <InlineImageText text={textContent.replace(/\[Image \d+:[^\]]*\]\s*/g, '').trim()} />
            </div>
          ) : null
        ) : (
          <>
            {orderedSegments.map((seg, idx) => {
              if (seg.type === 'text') {
                // Apply think-block stripping only to the first text segment
                let segText = seg.content;
                if (idx === 0 && thinkMatch) {
                  segText = segText.slice(thinkMatch[0].length);
                } else if (idx === 0 && isThinkingStreaming) {
                  segText = '';
                }
                if (!segText.trim()) return null;
                const isLastSegment = idx === orderedSegments.length - 1;
                return (
                  <div key={`text-${idx}`} style={{
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), 0 2px 16px rgba(0,0,0,0.12)',
                    color: '#dddada',
                    borderRadius: idx === 0 ? '4px 18px 18px 18px' : '18px',
                    padding: '12px 16px',
                    fontSize: 14, lineHeight: 1.65, wordBreak: 'break-word',
                    marginTop: idx > 0 ? 6 : 0,
                  }}>
                    {isStreaming && isLastSegment ? (
                      <InlineImageText text={segText} />
                    ) : (
                      <div className="chat-markdown">
                        <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                          {segText}
                        </Markdown>
                      </div>
                    )}
                  </div>
                );
              }
              if (seg.type === 'tool') {
                return (
                  <div key={seg.part.toolCallId} style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <ToolCallCard
                      tool={seg.part}
                      message={message}
                      completedImages={completedImages}
                      onUseAsInput={onUseAsInput}
                      onImageAction={onImageAction}
                      devMode={devMode}
                    />
                  </div>
                );
              }
              return null;
            })}
            {/* Thinking indicator — shows when streaming assistant message has no content yet */}
            {isStreaming && !textContent?.trim() && orderedSegments.length === 0 && !isThinkingStreaming && !thinkContent && (
              <div style={{
                padding: '10px 16px', borderRadius: '4px 18px 18px 18px',
                background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)' }}>Thinking</span>
                  <span style={{ display: 'flex', gap: 4 }}>
                    {[0, 1, 2].map(i => (
                      <span key={i} style={{
                        width: 5, height: 5, borderRadius: '50%',
                        background: 'rgba(255,126,103,0.6)',
                        animation: `pulse 1.4s ease-in-out ${i * 0.2}s infinite`,
                      }} />
                    ))}
                  </span>
                </div>
              </div>
            )}

            {/* Extra generation cards for multi-format jobs not covered by a tool card */}
            {extraJobIds.length > 0 && (
              <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {extraJobIds.map(({ jobId }) => (
                  <ExtraGenerationCard
                    key={jobId}
                    jobId={jobId}
                    completedImages={completedImages}
                    onUseAsInput={onUseAsInput}
                    onImageAction={onImageAction}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* Quick-reply buttons — shown when agent asks for confirmation */}
        {!isUser && textContent && onQuickReply && /\b(say \*\*go\*\*|ready to generate|shall i|want me to|go ahead)\b/i.test(textContent) && (
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            {['Go ahead', "Don't generate", 'Change prompt'].map((label) => (
              <button
                key={label}
                onClick={() => onQuickReply(label)}
                className="quick-chip"
                style={{
                  background: label === 'Go ahead'
                    ? 'rgba(255,126,103,0.12)'
                    : 'rgba(255,255,255,0.04)',
                  border: label === 'Go ahead'
                    ? '1px solid rgba(255,126,103,0.35)'
                    : '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 20,
                  padding: '6px 14px',
                  color: label === 'Go ahead' ? '#FF7E67' : 'rgba(255,255,255,0.5)',
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 150ms ease',
                }}
              >
                {label === 'Go ahead' ? '✨ ' : ''}{label}
              </button>
            ))}
          </div>
        )}

        {/* Ask-user question cards */}
        {!isUser && userQuestionsParts.length > 0 && (
          <div style={{ marginTop: 10 }}>
            {userQuestionsParts.map((part, i) => (
              <UserQuestionsCard
                key={i}
                data={part.data}
                onSubmit={onQuickReply}
                isLastMessage={isLastMessage}
                isStreaming={isStreaming}
              />
            ))}
          </div>
        )}

        {/* Asset verification cards */}
        {!isUser && assetVerifyParts.length > 0 && (
          <div style={{ marginTop: 10 }}>
            {assetVerifyParts.map((part, i) => (
              <AssetVerifyCard
                key={i}
                data={part.data}
                onSubmit={onQuickReply}
                isLastMessage={isLastMessage}
                isStreaming={isStreaming}
                openAssetPicker={openAssetPicker}
              />
            ))}
          </div>
        )}

        {/* Step metrics bar — token count, tools, timing */}
        {!isUser && stepMetricsParts.length > 0 && (
          <MessageMetricsBar steps={stepMetricsParts.map(p => p.data)} />
        )}
      </div>
    </div>
  );
}

export default memo(ChatMessage, (prev, next) => {
  // Always re-render the actively streaming message
  if (prev.isStreaming || next.isStreaming) return false;
  // Re-render if a job completed (image results arrived)
  if (prev.completedImages !== next.completedImages) return false;
  // Re-render when isLastMessage changes (card active/collapsed state)
  if (prev.isLastMessage !== next.isLastMessage) return false;
  // Re-render when onQuickReply becomes available (streaming ends)
  if (prev.onQuickReply !== next.onQuickReply) return false;
  // Skip re-render for all stable completed messages
  return prev.message.id === next.message.id;
});


function ToolCallCard({ tool, message, completedImages, onUseAsInput, onImageAction, devMode }) {
  const [viewer, setViewer] = useState({ open: false, index: 0 });

  const toolName = tool.type?.startsWith('tool-') ? tool.type.substring(5) : (tool.type || 'unknown');
  const isDone = tool.state === 'output-available';
  const isActive = tool.state === 'input-available';
  const toolInput = tool.input || tool.args;
  const toolOutput = tool.output;

  const isGen = GEN_TOOLS.has(toolName);
  const isCopyTool = toolName === 'generateMarketingCopyTool' || toolName === 'generate-marketing-copy';

  // Smart card routing (non-dev mode)
  if (!devMode) {
    // Copy package tool → rich card
    if (isCopyTool && isDone && toolOutput) {
      return <CopyPackageCard output={toolOutput} />;
    }
    // Generation tool still pending → progress card
    if (isGen && !isDone) {
      return <GenerationStatusCard toolInput={toolInput} isDone={false} isFailed={false} />;
    }
    // Generation tool failed → error card
    if (isGen && isDone && (toolOutput?.error || toolOutput?.success === false)) {
      return <GenerationStatusCard toolInput={toolInput} isDone={false} isFailed={true} failMessage={toolOutput?.message || toolOutput?.error} />;
    }
  }

  // Tool-level failure: the tool itself errored (validation, API error, exception)
  const isToolFailed = toolOutput?.error === true || toolOutput?.success === false;
  const isErrorResult = isToolFailed;

  // Batch generation: collect ALL job IDs (image_job_ids array + single image_job_id)
  const allJobIds = [
    ...(toolOutput?.data?.image_job_ids || []),
    toolOutput?.image_job_id,
    toolOutput?.workflow_job_id,
    toolOutput?.data?.image_job_id,
    toolOutput?.data?.workflow_job_id,
  ].filter(Boolean);

  // Fallback to data-generation-started events if no job IDs in tool output.
  // ONLY for generation tools — non-gen tools should never inherit image job IDs.
  if (allJobIds.length === 0 && !isErrorResult && isGen) {
    const genParts = message?.parts?.filter(p => p.type === 'data-generation-started') || [];
    for (const gp of genParts) {
      const jid = gp.data?.image_job_id || gp.data?.workflow_job_id;
      if (jid && !allJobIds.includes(jid)) allJobIds.push(jid);
    }
  }

  // Deduplicate
  const uniqueJobIds = [...new Set(allJobIds)];
  const jobId = uniqueJobIds[0] || undefined;

  // Collect ALL completed images across all batch job IDs
  const allCompletedUrls = uniqueJobIds.flatMap(jid => completedImages[jid]?.urls || []);
  const pendingJobCount = uniqueJobIds.filter(jid => !completedImages[jid] || completedImages[jid]?.status === 'processing').length;
  const completed = jobId ? completedImages[jobId] : null;
  const isImageReady = allCompletedUrls.length > 0;
  const isFailed = uniqueJobIds.some(jid => completedImages[jid]?.status === 'failed') && allCompletedUrls.length === 0;
  const isPending = isGen && !isImageReady && !isFailed && !isToolFailed;
  const totalSlots = allCompletedUrls.length + pendingJobCount;

  return (
    <>
      {devMode ? (
        <details
          open={isActive || isToolFailed}
          style={{
            marginBottom: 4, fontSize: 12,
            borderRadius: 8, padding: '2px 0',
          }}
        >
          <summary style={{
            cursor: 'pointer', userSelect: 'none', listStyle: 'none',
            display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px',
            borderRadius: 8,
            background: isToolFailed ? 'rgba(248,113,113,0.06)' : 'rgba(255,255,255,0.02)',
            border: `1px solid ${isToolFailed ? 'rgba(248,113,113,0.15)' : 'rgba(255,255,255,0.06)'}`,
            color: isToolFailed ? '#f87171' : isDone ? 'rgba(255,255,255,0.4)' : 'rgba(255,126,103,0.7)',
          }}>
            {isPending || isActive ? (
              <div style={{ width: 12, height: 12, borderRadius: '50%', border: '2px solid rgba(255,126,103,0.3)', borderTopColor: '#FF7E67', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
            ) : isToolFailed ? (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
            ) : (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            )}
            <span style={{ fontSize: 12, fontWeight: 500 }}>{getToolDisplayName(toolName)}</span>
            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.2)', fontFamily: 'monospace' }}>{toolName}</span>
          </summary>

          <div style={{
            marginTop: 4, background: 'rgba(0,0,0,0.4)',
            border: '1px solid rgba(255,255,255,0.07)', borderRadius: 6,
            padding: '8px 10px', overflow: 'auto', maxHeight: 320,
          }}>
            {toolInput && (
              <>
                <div style={{ color: 'rgba(255,255,255,0.3)', marginBottom: 4, fontSize: 10, letterSpacing: '0.06em' }}>INPUT</div>
                <pre style={{ margin: '0 0 8px', color: 'rgba(255,255,255,0.6)', fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {JSON.stringify(toolInput, null, 2)}
                </pre>
              </>
            )}
            {toolOutput && (
              <>
                <div style={{ color: 'rgba(255,255,255,0.3)', marginBottom: 4, fontSize: 10, letterSpacing: '0.06em' }}>OUTPUT</div>
                <pre style={{ margin: 0, color: 'rgba(255,255,255,0.6)', fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {typeof toolOutput === 'string' ? toolOutput : JSON.stringify(toolOutput, null, 2)}
                </pre>
              </>
            )}
          </div>
        </details>
      ) : (
        /* Production tool card — clean, friendly names, proper styling */
        isToolFailed ? (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 12px', borderRadius: 10,
            background: 'rgba(248,113,113,0.06)', border: '1px solid rgba(248,113,113,0.15)',
            fontSize: 12, color: '#f87171',
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
            </svg>
            <span>{toolOutput?.message || `${getToolDisplayName(toolName)} failed`}</span>
          </div>
        ) : isPending || isActive ? (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '6px 12px', borderRadius: 10,
            background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)',
            fontSize: 12, color: 'rgba(255,255,255,0.35)',
          }}>
            <div style={{
              width: 12, height: 12, borderRadius: '50%',
              border: '2px solid rgba(255,126,103,0.3)', borderTopColor: '#FF7E67',
              animation: 'spin 0.8s linear infinite', flexShrink: 0,
            }} />
            <span>{getToolDisplayName(toolName)}...</span>
          </div>
        ) : null /* completed tools: hidden in production — results speak for themselves */
      )}

      {/* Image / video results — batch variations shown in one grid */}
      {(isImageReady || pendingJobCount > 0) && (
        <div style={{ marginBottom: 4 }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: totalSlots >= 3 ? 'repeat(3, 1fr)' : totalSlots === 2 ? 'repeat(2, 1fr)' : '1fr',
            gap: 8,
            maxWidth: totalSlots >= 3 ? 520 : totalSlots === 2 ? 360 : 280,
          }}>
            {allCompletedUrls.map((url, i) => {
              const isVideo = /\.(mp4|webm|mov)(\?|$)/i.test(url);
              if (isVideo) {
                return <video key={i} src={url} controls loop playsInline style={{ width: '100%', borderRadius: 8, border: '1px solid rgba(255,255,255,0.09)', display: 'block' }} />;
              }
              return <ImageWithOverlay key={i} url={url} onAction={onImageAction} onClick={() => setViewer({ open: true, index: i })} />;
            })}
            {/* Pending placeholders for jobs still processing */}
            {Array.from({ length: pendingJobCount }, (_, i) => (
              <div key={`pending-${i}`} style={{
                aspectRatio: '4/5',
                borderRadius: 10,
                border: '1px solid rgba(255,255,255,0.09)',
                background: 'rgba(255,255,255,0.03)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <div style={{
                  width: 20, height: 20, borderRadius: '50%',
                  border: '2px solid rgba(255,126,103,0.4)',
                  borderTopColor: '#FF7E67',
                  animation: 'spin 0.8s linear infinite',
                }} />
              </div>
            ))}
          </div>
          {allCompletedUrls.length > 0 && (
            <FeedbackButtons jobId={jobId} jobType={completed?.jobType} onUseAsInput={onUseAsInput} imageUrl={allCompletedUrls[0]} />
          )}
        </div>
      )}
      {isFailed && (
        <div style={{ paddingLeft: 10, marginBottom: 4, fontSize: 11, color: '#f87171', fontFamily: 'inherit' }}>
          ✕ {completed?.error || 'Generation failed'}
        </div>
      )}
      {isToolFailed && (
        <div style={{ paddingLeft: 10, marginBottom: 4, fontSize: 11, color: '#f87171', fontFamily: 'inherit' }}>
          ✕ {toolOutput?.message || toolOutput?.error_message || 'Tool call failed'}
        </div>
      )}

      <ImageViewerModal
        isOpen={viewer.open}
        images={allCompletedUrls}
        initialIndex={viewer.index}
        onClose={() => setViewer(v => ({ ...v, open: false }))}
        onUseAsInput={onUseAsInput}
      />
    </>
  );
}

// ─── Feedback Buttons (like/dislike/use as input) ────────────────────────────

// ─── Image with hover overlay actions ────────────────────────────────────────

function ImageWithOverlay({ url, onAction, onClick }) {
  const [hovered, setHovered] = useState(false);
  const [btnHover, setBtnHover] = useState(null);

  const actions = [
    { id: 'use-as-input', label: 'Use as input' },
    { id: 'upscale',      label: 'Upscale' },
    { id: 'remove-bg',    label: 'Remove BG' },
  ];

  const handleDownload = async (e) => {
    e.stopPropagation();
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const ext = blob.type.split('/')[1] || 'png';
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `uro-generated.${ext}`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { /* non-critical */ }
  };

  const overlayBtnStyle = (id) => ({
    background: btnHover === id ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.11)',
    backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: 6,
    color: '#fff',
    fontSize: 10,
    fontWeight: 500,
    padding: '4px 8px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'background 100ms ease',
    letterSpacing: '0.02em',
  });

  return (
    <div
      style={{ position: 'relative', cursor: 'pointer', display: 'inline-block' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setBtnHover(null); }}
    >
      <img
        src={url}
        alt=""
        style={{
          maxWidth: '100%', maxHeight: 360, borderRadius: 10,
          border: '1px solid rgba(255,255,255,0.09)',
          objectFit: 'contain', display: 'block',
          transition: 'transform 200ms ease, box-shadow 200ms ease',
          transform: hovered ? 'scale(1.015)' : 'scale(1)',
          boxShadow: hovered ? '0 8px 32px rgba(0,0,0,0.45)' : 'none',
        }}
      />
      {hovered && (
        <div
          onClick={onClick}
          style={{
            position: 'absolute', inset: 0, borderRadius: 10,
            background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.1) 50%, transparent 100%)',
            display: 'flex', alignItems: 'flex-end',
            padding: '8px',
            gap: 5,
          }}
        >
          {/* Download button — top right */}
          <button
            title="Download image"
            onClick={handleDownload}
            onMouseEnter={() => setBtnHover('download')}
            onMouseLeave={() => setBtnHover(null)}
            style={{
              position: 'absolute', top: 8, right: 8,
              width: 34, height: 34, borderRadius: 10,
              background: btnHover === 'download' ? 'rgba(0,0,0,0.8)' : 'rgba(0,0,0,0.6)',
              backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 150ms ease',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </button>

          {/* Bottom action buttons */}
          {onAction && actions.map(btn => (
            <button
              key={btn.id}
              onClick={(e) => { e.stopPropagation(); onAction(url, btn.id); }}
              onMouseEnter={() => setBtnHover(btn.id)}
              onMouseLeave={() => setBtnHover(null)}
              style={overlayBtnStyle(btn.id)}
            >
              {btn.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Extra generation card (for multi-format jobs from data-generation-started) ──

function ExtraGenerationCard({ jobId, completedImages, onUseAsInput, onImageAction }) {
  const [viewer, setViewer] = useState({ open: false, index: 0 });
  const completed = jobId ? completedImages[jobId] : null;
  const isFailed = completed?.status === 'failed';
  const isImageReady = completed?.urls?.length > 0;
  const isPending = !isImageReady && !isFailed;
  const dotColor = isFailed ? '#f87171' : isImageReady ? '#4ade80' : '#38BDF8';

  return (
    <>
      <div style={{
        marginTop: 8, borderRadius: 14, overflow: 'hidden',
        background: 'rgba(255,255,255,0.04)',
        backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
        border: isFailed ? '1px solid rgba(248,113,113,0.2)' : '1px solid rgba(255,255,255,0.08)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
      }}>
        {!isImageReady && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px' }}>
            <span style={{
              width: 8, height: 8, borderRadius: '50%', background: dotColor,
              display: 'inline-block', flexShrink: 0,
              boxShadow: isPending ? `0 0 6px ${dotColor}` : 'none',
              animation: isPending ? 'pulse 1.2s ease-in-out infinite' : 'none',
            }} />
            <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, fontWeight: 500 }}>
              {isFailed ? 'Generation failed' : 'Generating your image…'}
            </span>
          </div>
        )}
        {isPending && (
          <div style={{ height: 2, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: '30%',
              background: 'linear-gradient(90deg, transparent, #FF7E67, #FF4D8D, transparent)',
              animation: 'shimmer 1.8s ease-in-out infinite',
            }} />
          </div>
        )}
        {isImageReady && (
          <div style={{ padding: '8px 10px 10px', display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {completed.urls.map((url, i) => (
                <ImageWithOverlay
                  key={i}
                  url={url}
                  onAction={onImageAction}
                  onClick={() => setViewer({ open: true, index: i })}
                />
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 0' }}>
              <FeedbackButtons jobId={jobId} jobType={completed?.jobType} onUseAsInput={onUseAsInput} imageUrl={completed.urls[0]} />
            </div>
          </div>
        )}
      </div>
      <ImageViewerModal
        isOpen={viewer.open}
        images={completed?.urls || []}
        initialIndex={viewer.index}
        onClose={() => setViewer(v => ({ ...v, open: false }))}
        onUseAsInput={onUseAsInput}
      />
    </>
  );
}

// ─── Message metrics bar (tokens, steps, tools) ──────────────────────────────

function MessageMetricsBar({ steps }) {
  const [expanded, setExpanded] = useState(false);

  const totals = steps.reduce((acc, s) => {
    acc.promptTokens += s?.usage?.prompt_tokens || 0;
    acc.completionTokens += s?.usage?.completion_tokens || 0;
    acc.totalTokens += s?.usage?.total_tokens || (s?.usage?.prompt_tokens || 0) + (s?.usage?.completion_tokens || 0);
    (s?.tool_calls || []).forEach(tc => {
      if (tc.tool_name && !acc.tools.includes(tc.tool_name)) acc.tools.push(tc.tool_name);
    });
    return acc;
  }, { promptTokens: 0, completionTokens: 0, totalTokens: 0, tools: [] });

  const formatNum = (n) => n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);

  return (
    <div style={{ marginTop: 6 }}>
      <button
        onClick={() => setExpanded(e => !e)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          display: 'flex', alignItems: 'center', gap: 8,
        }}
      >
        <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.2)', fontFamily: 'monospace', letterSpacing: '0.04em' }}>
          {totals.totalTokens > 0 && `${formatNum(totals.totalTokens)} tokens`}
          {totals.tools.length > 0 && ` · ${totals.tools.length} tool${totals.tools.length > 1 ? 's' : ''}`}
          {steps.length > 1 && ` · ${steps.length} steps`}
          {' '}
          <span style={{ opacity: 0.5 }}>{expanded ? '▲' : '▼'}</span>
        </span>
      </button>
      {expanded && (
        <div style={{
          marginTop: 4, padding: '6px 10px', borderRadius: 6,
          background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.06)',
          fontSize: 10, fontFamily: 'monospace', color: 'rgba(255,255,255,0.35)',
        }}>
          {steps.map((s, i) => (
            <div key={i} style={{ marginBottom: i < steps.length - 1 ? 4 : 0 }}>
              <span style={{ color: 'rgba(255,255,255,0.2)' }}>step {s.step_index ?? i} · </span>
              <span style={{ color: 'rgba(148,163,184,0.6)' }}>{s.step_type}</span>
              {s.usage?.total_tokens != null && (
                <span> · {formatNum(s.usage.total_tokens)} tok ({s.usage.prompt_tokens}↑ {s.usage.completion_tokens}↓)</span>
              )}
              {s.finish_reason && <span style={{ color: 'rgba(255,255,255,0.2)' }}> · {s.finish_reason}</span>}
              {s.tool_calls?.length > 0 && (
                <div style={{ paddingLeft: 8, color: 'rgba(74,222,128,0.4)' }}>
                  {s.tool_calls.map((tc, j) => <span key={j}> → {tc.tool_name}</span>)}
                </div>
              )}
            </div>
          ))}
          {totals.promptTokens > 0 && (
            <div style={{ marginTop: 4, paddingTop: 4, borderTop: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.2)' }}>
              total: {formatNum(totals.totalTokens)} ({formatNum(totals.promptTokens)}↑ {formatNum(totals.completionTokens)}↓)
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── UserQuestionsCard (ask-user tool UI) ────────────────────────────────────

function UserQuestionsCard({ data, onSubmit, isLastMessage = false, isStreaming = false }) {
  const questions = data?.questions || [];
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [customText, setCustomText] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const currentQ = questions[currentIdx];
  const isLast = currentIdx === questions.length - 1;
  const isAnswered = (qId) => answers[qId] !== undefined;

  const handleSelect = (qId, value, label) => {
    if (value === '__custom__') return;
    setAnswers(prev => ({ ...prev, [qId]: label }));
    setCustomText('');
    if (!isLast) {
      setTimeout(() => setCurrentIdx(i => i + 1), 180);
    }
  };

  const handleCustomSubmit = (qId) => {
    const val = customText.trim();
    if (!val) return;
    setAnswers(prev => ({ ...prev, [qId]: val }));
    setCustomText('');
    if (!isLast) {
      setTimeout(() => setCurrentIdx(i => i + 1), 180);
    }
  };

  const handleFinish = () => {
    if (!onSubmit) return;
    const lines = questions.map(q => {
      const ans = answers[q.id];
      return ans ? `${q.text}: User said "${ans}"` : null;
    }).filter(Boolean);
    const text = `I have answered your questions:\n${lines.join('\n')}`;
    setSubmitted(true);
    onSubmit(text);
  };

  if (!currentQ) return null;

  if (submitted || !isLastMessage) {
    return (
      <div style={{
        padding: '8px 12px', borderRadius: 10, marginTop: 4,
        background: 'rgba(74,222,128,0.05)', border: '1px solid rgba(74,222,128,0.15)',
        fontSize: 11, color: 'rgba(74,222,128,0.5)', fontFamily: 'monospace',
      }}>
        ✓ Answered
      </div>
    );
  }

  if (isStreaming) {
    return (
      <div style={{
        padding: '10px 14px', borderRadius: 10, marginTop: 4,
        background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
        fontSize: 11, color: 'rgba(148,163,184,0.5)', fontFamily: 'monospace',
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: 'rgba(56,189,248,0.5)', animation: 'pulse 1.2s ease-in-out infinite' }} />
        Preparing questions...
      </div>
    );
  }

  return (
    <div style={{
      borderRadius: 14, overflow: 'hidden',
      background: 'rgba(255,255,255,0.04)',
      border: '1px solid rgba(255,255,255,0.1)',
      boxShadow: '0 2px 16px rgba(0,0,0,0.15)',
    }}>
      {/* Context header */}
      {data?.context && (
        <div style={{
          padding: '10px 14px 6px',
          fontSize: 12, color: 'rgba(255,255,255,0.45)', fontStyle: 'italic',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}>
          {data.context}
        </div>
      )}

      {/* Progress dots */}
      {questions.length > 1 && (
        <div style={{ display: 'flex', gap: 4, padding: '8px 14px 0' }}>
          {questions.map((_, i) => (
            <span key={i} style={{
              width: 5, height: 5, borderRadius: '50%', flexShrink: 0,
              background: i < currentIdx ? '#4ade80' : i === currentIdx ? '#FF7E67' : 'rgba(255,255,255,0.12)',
              transition: 'background 200ms ease',
            }} />
          ))}
        </div>
      )}

      {/* Current question */}
      <div style={{ padding: '10px 14px 12px' }}>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', fontWeight: 500, marginBottom: 10 }}>
          {currentQ.text}
        </div>

        {/* Options */}
        {(currentQ.options || []).map(opt => {
          const chosen = answers[currentQ.id] === opt.label;
          return (
            <button
              key={opt.value}
              onClick={() => handleSelect(currentQ.id, opt.value, opt.label)}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                marginBottom: 6, padding: '8px 12px', borderRadius: 10, cursor: 'pointer',
                background: chosen ? 'rgba(255,126,103,0.14)' : 'rgba(255,255,255,0.04)',
                border: chosen ? '1px solid rgba(255,126,103,0.45)' : '1px solid rgba(255,255,255,0.1)',
                color: chosen ? '#FF7E67' : 'rgba(255,255,255,0.65)',
                fontSize: 13, transition: 'all 150ms ease',
              }}
            >
              <span style={{ fontWeight: 500 }}>{opt.label}</span>
              {opt.reason && (
                <span style={{ fontSize: 11, opacity: 0.5, marginLeft: 6 }}>{opt.reason}</span>
              )}
            </button>
          );
        })}

        {/* Custom input */}
        {currentQ.allow_custom && (
          <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
            <input
              value={customText}
              onChange={e => setCustomText(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCustomSubmit(currentQ.id)}
              placeholder="Other…"
              style={{
                flex: 1, background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8,
                padding: '7px 10px', fontSize: 12, color: 'rgba(255,255,255,0.7)',
                outline: 'none',
              }}
            />
            <button
              onClick={() => handleCustomSubmit(currentQ.id)}
              disabled={!customText.trim()}
              style={{
                padding: '7px 12px', borderRadius: 8, cursor: customText.trim() ? 'pointer' : 'default',
                background: customText.trim() ? 'rgba(255,126,103,0.14)' : 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,126,103,0.25)',
                color: customText.trim() ? '#FF7E67' : 'rgba(255,255,255,0.2)',
                fontSize: 12, transition: 'all 150ms ease',
              }}
            >OK</button>
          </div>
        )}

        {/* Next / Finish */}
        {isAnswered(currentQ.id) && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            {isLast ? (
              <button
                onClick={handleFinish}
                style={{
                  padding: '7px 18px', borderRadius: 20, cursor: 'pointer',
                  background: 'linear-gradient(to right, rgba(255,126,103,0.25), rgba(255,77,141,0.2))',
                  border: '1px solid rgba(255,126,103,0.4)',
                  color: '#FF7E67', fontSize: 12, fontWeight: 600,
                  transition: 'all 150ms ease',
                }}
              >Send answers ✓</button>
            ) : (
              <button
                onClick={() => setCurrentIdx(i => i + 1)}
                style={{
                  padding: '6px 14px', borderRadius: 20, cursor: 'pointer',
                  background: 'rgba(255,126,103,0.08)', border: '1px solid rgba(255,126,103,0.22)',
                  color: 'rgba(255,126,103,0.75)', fontSize: 12,
                  transition: 'all 150ms ease',
                }}
              >Next →</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Asset Verify Card (verify-assets tool UI) ────────────────────────────────

function AssetVerifyCard({ data, onSubmit, isLastMessage = false, isStreaming = false, openAssetPicker }) {
  const [selected, setSelected] = useState(() =>
    (data?.selected_assets || []).map(a => ({ ...a }))
  );
  const [recommendations] = useState(data?.recommendations || []);
  const [imgErrors, setImgErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [additionalNotes, setAdditionalNotes] = useState('');

  const handleRemove = (url) => setSelected(prev => prev.filter(a => a.url !== url));

  const handleAddRec = (asset) => {
    if (!selected.find(a => a.url === asset.url)) {
      setSelected(prev => [...prev, { ...asset }]);
    }
  };

  const handleProceed = () => {
    if (!onSubmit || selected.length === 0) return;
    const lines = selected.map((a, i) => `${i + 1}. ${a.url}${a.label ? ` (${a.label})` : ''}`);
    const notes = additionalNotes.trim();
    const text = `Approved assets for image generation:\n${lines.join('\n')}\nPlease use exactly these URLs as image_urls.${notes ? `\n\nAdditional notes: ${notes}` : ''}`;
    setSubmitted(true);
    onSubmit(text);
  };

  if (submitted || !isLastMessage) {
    return (
      <div style={{
        padding: '8px 12px', borderRadius: 10, marginTop: 4,
        background: 'rgba(74,222,128,0.05)', border: '1px solid rgba(74,222,128,0.15)',
        fontSize: 11, color: 'rgba(74,222,128,0.5)', fontFamily: 'monospace',
      }}>
        ✓ Assets approved ({selected.length})
      </div>
    );
  }

  if (isStreaming) {
    return (
      <div style={{
        padding: '10px 14px', borderRadius: 10, marginTop: 4,
        background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
        fontSize: 11, color: 'rgba(148,163,184,0.5)', fontFamily: 'monospace',
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: 'rgba(56,189,248,0.5)', animation: 'pulse 1.2s ease-in-out infinite' }} />
        Preparing assets...
      </div>
    );
  }

  const isAdded = (url) => !!selected.find(a => a.url === url);

  return (
    <div style={{
      borderRadius: 14, overflow: 'hidden',
      background: 'rgba(240,147,251,0.04)',
      border: '1px solid rgba(240,147,251,0.2)',
      boxShadow: '0 2px 16px rgba(0,0,0,0.15)',
    }}>
      {/* Header */}
      {data?.context && (
        <div style={{
          padding: '10px 14px 8px',
          fontSize: 12, color: 'rgba(255,255,255,0.45)', fontStyle: 'italic',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}>
          {data.context}
        </div>
      )}

      <div style={{ padding: '10px 14px 12px' }}>
        {/* Selected assets */}
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', letterSpacing: '0.06em', marginBottom: 8, textTransform: 'uppercase' }}>
          Selected assets ({selected.length})
        </div>
        {selected.length === 0 && (
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.2)', marginBottom: 10, fontStyle: 'italic' }}>No assets selected</div>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          {selected.map((asset) => (
            <div key={asset.url} style={{
              position: 'relative', borderRadius: 10, overflow: 'hidden',
              border: '1px solid rgba(240,147,251,0.3)',
              background: 'rgba(0,0,0,0.3)', flexShrink: 0,
            }}>
              {imgErrors[asset.url] ? (
                <div style={{ width: 72, height: 72, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🖼️</div>
              ) : (
                <img
                  src={asset.url} alt={asset.label || ''}
                  onError={() => setImgErrors(e => ({ ...e, [asset.url]: true }))}
                  style={{ width: 72, height: 72, objectFit: 'cover', display: 'block' }}
                />
              )}
              {asset.label && (
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  background: 'rgba(0,0,0,0.7)', fontSize: 9, color: 'rgba(255,255,255,0.7)',
                  padding: '2px 4px', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>{asset.label}</div>
              )}
              <button
                onClick={() => handleRemove(asset.url)}
                title="Remove"
                style={{
                  position: 'absolute', top: 3, right: 3,
                  width: 18, height: 18, borderRadius: '50%', border: 'none', cursor: 'pointer',
                  background: 'rgba(248,113,113,0.85)', color: '#fff',
                  fontSize: 10, fontWeight: 700, lineHeight: '18px', textAlign: 'center',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                }}
              >✕</button>
            </div>
          ))}
        </div>

        {/* Recommendations */}
        {recommendations.length > 0 && (
          <>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', letterSpacing: '0.06em', marginBottom: 8, textTransform: 'uppercase' }}>
              Agent suggests
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
              {recommendations.map((rec) => {
                const added = isAdded(rec.url);
                return (
                  <div key={rec.url} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '6px 10px', borderRadius: 10,
                    background: added ? 'rgba(255,126,103,0.08)' : 'rgba(255,255,255,0.03)',
                    border: added ? '1px solid rgba(255,126,103,0.22)' : '1px solid rgba(255,255,255,0.07)',
                    transition: 'all 150ms ease',
                  }}>
                    {imgErrors[rec.url] ? (
                      <div style={{ width: 36, height: 36, borderRadius: 6, background: 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>🖼️</div>
                    ) : (
                      <img
                        src={rec.url} alt={rec.label || ''}
                        onError={() => setImgErrors(e => ({ ...e, [rec.url]: true }))}
                        style={{ width: 36, height: 36, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }}
                      />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{rec.label}</div>
                      {rec.reason && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginTop: 1 }}>{rec.reason}</div>}
                    </div>
                    <button
                      onClick={() => added ? handleRemove(rec.url) : handleAddRec(rec)}
                      style={{
                        padding: '4px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 11, fontWeight: 600, flexShrink: 0,
                        background: added ? 'rgba(248,113,113,0.12)' : 'rgba(74,222,128,0.12)',
                        border: added ? '1px solid rgba(248,113,113,0.3)' : '1px solid rgba(74,222,128,0.3)',
                        color: added ? '#f87171' : '#4ade80',
                        transition: 'all 150ms ease',
                      }}
                    >{added ? '− Remove' : '+ Add'}</button>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Add asset via picker modal */}
        {openAssetPicker && (
          <button
            onClick={() => openAssetPicker((assets) => {
              if (!assets?.length) return;
              const toAdd = assets
                .filter(a => !selected.find(s => s.url === a.url))
                .map(a => ({ url: a.url, label: a.name || a.asset_type || 'Asset', reason: 'Added by user' }));
              setSelected(prev => [...prev, ...toAdd]);
            })}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '5px 12px', borderRadius: 8, marginBottom: 10,
              background: 'rgba(255,126,103,0.08)',
              border: '1px solid rgba(255,126,103,0.25)',
              color: 'rgba(255,126,103,0.8)', fontSize: 11, fontWeight: 500,
              cursor: 'pointer', fontFamily: 'inherit', transition: 'all 150ms ease',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,126,103,0.15)'; e.currentTarget.style.borderColor = 'rgba(255,126,103,0.45)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,126,103,0.08)'; e.currentTarget.style.borderColor = 'rgba(255,126,103,0.25)'; }}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" />
            </svg>
            + Add from assets
          </button>
        )}

        {/* Additional notes */}
        <textarea
          value={additionalNotes}
          onChange={e => setAdditionalNotes(e.target.value)}
          placeholder="Any additional notes for the agent... (optional)"
          rows={2}
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '8px 10px', borderRadius: 8, marginBottom: 10,
            background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
            color: 'rgba(255,255,255,0.7)', fontSize: 12, fontFamily: 'inherit',
            resize: 'vertical', minHeight: 36, outline: 'none',
            transition: 'border-color 150ms ease',
          }}
          onFocus={e => { e.target.style.borderColor = 'rgba(240,147,251,0.4)'; }}
          onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.08)'; }}
        />

        {/* Proceed button */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={handleProceed}
            disabled={selected.length === 0 || !onSubmit}
            style={{
              padding: '8px 20px', borderRadius: 20, cursor: selected.length > 0 && onSubmit ? 'pointer' : 'default',
              background: selected.length > 0 ? 'linear-gradient(to right, #FF7E67, #FF4D8D)' : 'rgba(255,255,255,0.04)',
              border: selected.length > 0 ? 'none' : '1px solid rgba(255,255,255,0.08)',
              color: selected.length > 0 ? '#fff' : 'rgba(255,255,255,0.2)',
              fontSize: 12, fontWeight: 600, transition: 'all 150ms ease',
            }}
          >
            Generate with {selected.length} asset{selected.length !== 1 ? 's' : ''} ✓
          </button>
        </div>
      </div>
    </div>
  );
}

